import { useState, useRef, useEffect } from 'react';
import { Form, ListGroup } from 'react-bootstrap';
import type { CategoryOption } from '../../../apps/hooks/oms/useProductCatTaxonomy';

interface CategoryPickerProps {
  options: CategoryOption[];
  onSelect: (option: CategoryOption) => void;
  isLoading?: boolean;
  disabled?: boolean;
  size?: 'sm' | 'lg';
}

const MAX_RESULTS = 30;

const CategoryPicker = ({ options, onSelect, isLoading, disabled, size }: CategoryPickerProps) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const trimmedQuery = query.trim();
  const filtered =
    trimmedQuery.length > 0
      ? options
          .filter(
            (option) =>
              option.fullPath.toLowerCase().includes(trimmedQuery.toLowerCase()) ||
              option.slug.toLowerCase().includes(trimmedQuery.toLowerCase()),
          )
          .slice(0, MAX_RESULTS)
      : [];
  const hasExactSlug = options.some((option) => option.slug.toLowerCase() === trimmedQuery.toLowerCase());
  const showCustomOption = trimmedQuery.length > 0 && !hasExactSlug;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isDisabled = disabled ?? isLoading ?? false;

  const handleSelect = (option: CategoryOption) => {
    if (isDisabled) {
      return;
    }
    onSelect(option);
    setQuery('');
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <Form.Control
        type="text"
        {...(size ? { size } : {})}
        placeholder={isLoading ? 'Loading categories...' : 'Search or type a category slug...'}
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setIsOpen(true);
        }}
        onFocus={() => {
          if (trimmedQuery.length > 0) {
            setIsOpen(true);
          }
        }}
        disabled={isDisabled}
      />
      {!isDisabled && isOpen && (filtered.length > 0 || showCustomOption) && (
        <ListGroup
          style={{
            position: 'absolute',
            zIndex: 1000,
            width: '100%',
            maxHeight: '260px',
            overflowY: 'auto',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        >
          {filtered.map((option) => (
            <ListGroup.Item
              key={option.termId}
              action
              onClick={() => handleSelect(option)}
              style={{ cursor: 'pointer' }}
            >
              <div style={{ fontSize: '0.85rem' }}>{option.fullPath}</div>
              <small className="text-muted">{option.slug}</small>
            </ListGroup.Item>
          ))}
          {showCustomOption && (
            <ListGroup.Item
              action
              onClick={() => handleSelect({ termId: -1, slug: trimmedQuery, name: trimmedQuery, fullPath: '' })}
              style={{ cursor: 'pointer' }}
            >
              <div style={{ fontSize: '0.85rem' }}>
                Use custom slug: <code>{trimmedQuery}</code>
              </div>
              <small className="text-muted">Not in the current taxonomy — flags for review</small>
            </ListGroup.Item>
          )}
        </ListGroup>
      )}
    </div>
  );
};

export default CategoryPicker;
